import { useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORG_UNIT_KIND_MAP, type OrgUnitKind } from '@goyone/shared';
import { Badge, EmptyState, ErrorState, Notice, PageHead, Stat } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { UnitDialog, type UnitDraft } from './OrgUnits';

interface PositionChip {
  id: string;
  code: string | null;
  title: string;
  orgUnitId: string | null;
  reportsToId: string | null;
  reportsTo: string | null;
  headcount: number;
  filled: number;
  grade: { id: string; name: string; level: number } | null;
}

interface Node extends UnitDraft {
  kindLabel: string;
  kindTone: string;
  department: string | null;
  manager: string | null;
  costCenter: { id: string; code: string; name: string } | null;
  directCount: number;
  totalCount: number;
  approved: number;
  positions: PositionChip[];
  children: Node[];
}

interface Structure {
  roots: Node[];
  loosePositions: PositionChip[];
  managers: { id: string; name: string; jobTitle: string | null }[];
  withoutUnit: number;
}

/** ما يُحمل أثناء السحب — نوعُه يقرّر ما يقع عند الإفلات. */
type Drag =
  | { kind: 'unit'; id: string; name: string }
  | { kind: 'position'; id: string; title: string };

/**
 * الهيكل التنظيمي.
 *
 * ## ولماذا يُرسم بالسحب لا يُملأ بالقوائم
 *
 * «من يتبع من» سؤالٌ عن شكلٍ لا عن قيمةِ حقل. ومن يبني هيكلَ شركةٍ يجرّب:
 * يضع التسويقَ تحت التجاريّة، ثمّ يراه فيخرجه إلى الجذر، ثمّ يجمع تحته
 * المحتوى والإعلان. وقائمةٌ منسدلةٌ اسمها «تتبع» تجعل كلَّ تجربةٍ ثلاثَ
 * نقراتٍ وفتحَ نموذجٍ وإغلاقَه — فلا يجرّب أحدٌ ويبقى الهيكلُ على أوّل رسمٍ
 * خُطّ.
 *
 * ## وما يُسحب شيئان
 *
 * الوحدةُ تُسحب على وحدةٍ فتصير تحتها، والمسمّى الوظيفيُّ يُسحب من الجانب
 * إلى وحدةٍ فيصير فيها. وهما مختلفان في المعنى: الأولى تقول «هذا القسمُ
 * داخل تلك الإدارة»، والثاني يقول «هذا المنصبُ مكانه هنا» — ولذلك
 * يُرسمان بشكلين مختلفين لا بشكلٍ واحدٍ يُفرَّق بينهما بالتخمين.
 */
export function OrgStructure() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [drag, setDrag] = useState<Drag | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<
    { unit: UnitDraft | null; parent: UnitDraft | null } | null
  >(null);

  const structure = useQuery({
    queryKey: ['hr', 'setup', 'structure'],
    queryFn: () => api<Structure>('/hr/setup/structure'),
  });

  const moveUnit = useMutation({
    mutationFn: (v: { id: string; parentId: string | null }) =>
      api(`/hr/setup/units/${v.id}/move`, {
        method: 'PATCH',
        body: { parentId: v.parentId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('نُقلت الوحدة');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر النقل'),
  });

  const placePosition = useMutation({
    mutationFn: (v: { id: string; orgUnitId: string | null }) =>
      api(`/hr/setup/positions/${v.id}/place`, {
        method: 'PATCH',
        body: { orgUnitId: v.orgUnitId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('حُفظ الموضع');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const data = structure.data;
  const roots = data?.roots ?? [];
  const loose = data?.loosePositions ?? [];

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /*
   * الإفلاتُ على وحدة.
   *
   * ويُمنع إفلاتُ الوحدة على نفسها هنا لا في الخادم وحدَه: ردُّ الخادم صحيحٌ
   * ولكنّه يصل بعد أن يكون المستخدمُ قد رأى العقدةَ تقفز ثمّ تعود.
   */
  const dropOnUnit = (target: Node) => {
    if (!drag) return;
    if (drag.kind === 'unit') {
      if (drag.id === target.id) return;
      moveUnit.mutate({ id: drag.id, parentId: target.id });
    } else {
      placePosition.mutate({ id: drag.id, orgUnitId: target.id });
    }
    setDrag(null);
    setOver(null);
  };

  const dropOnRoot = () => {
    if (!drag) return;
    if (drag.kind === 'unit') moveUnit.mutate({ id: drag.id, parentId: null });
    else placePosition.mutate({ id: drag.id, orgUnitId: null });
    setDrag(null);
    setOver(null);
  };

  const zoneProps = (key: string, onDrop: () => void) => ({
    onDragOver: (e: DragEvent) => {
      if (!drag) return;
      /* المنعُ الافتراضيُّ هو ما يجعل المنطقةَ تقبل الإفلات أصلًا. */
      e.preventDefault();
      e.stopPropagation();
      if (over !== key) setOver(key);
    },
    onDragLeave: () => setOver((cur) => (cur === key ? null : cur)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDrop();
    },
  });

  return (
    <div className="page">
      <PageHead
        title="الهيكل التنظيمي"
        subtitle="ارسم الشركةَ بالسحب: الوحدةُ تُسحب على وحدةٍ فتصير تحتها، والمسمّى الوظيفيُّ يُسحب من الجانب إلى الوحدة التي مكانُه فيها"
        actions={
          <>
            {/*
              الرسمُ لا يعرض الموقوفةَ ولا يوقف وحدة: شجرةُ الهيكل تُرسم
              بالفعّال وحدَه، ومن أراد إيقافَ وحدةٍ أو رؤيةَ الموقوف فتح
              القائمة. وإخفاءُ الطريق إليها كان سيجعل الإيقافَ يبدو مفقودًا.
            */}
            <Link className="btn" to="/settings/org-units">
              <Icon name="layers" size={15} />
              قائمة الوحدات
            </Link>
            {canEdit && (
              <button
                className="btn btn-primary"
                onClick={() => setEditing({ unit: null, parent: null })}
              >
                <Icon name="plus" size={15} />
                وحدة جديدة
              </button>
            )}
          </>
        }
      />

      {structure.isError && (
        <ErrorState error={structure.error} onRetry={() => void structure.refetch()} />
      )}

      {structure.isPending && <div className="skeleton" style={{ height: 300 }} />}

      {data && (
        <div className="stats">
          <Stat label="وحدات" value={num(countUnits(roots))} note="الفعّالةُ وحدَها تُرسم" />
          <Stat
            label="مسمّيات موضوعة"
            value={num(countPositions(roots))}
            note={
              loose.length > 0
                ? `و${plural(loose.length, WORDS.position)} لم توضع بعد`
                : 'كلُّ المسمّيات لها موضعٌ في الهيكل'
            }
          />
          <Stat
            label="مقاعد معتمدة"
            value={num(sumApproved(roots))}
            note="مجموعُ ما اعتُمد لمسمّيات الهيكل — لا عددُ من فيها"
          />
          <Stat
            label="خارج الهيكل"
            value={num(data.withoutUnit)}
            note={
              data.withoutUnit > 0
                ? 'موظّفون على رأس العمل بلا وحدةٍ في ملفّاتهم'
                : 'كلُّ من على رأس العمل داخل الهيكل'
            }
          />
        </div>
      )}

      {!canEdit && (
        <Notice title="القراءةُ لك والرسمُ لمدير النظام" icon="eye">
          تعديلُ الهيكل يغيّر من يتبع من في الشركة كلِّها، ويظهر أثرُه في
          التقارير وفي كلفة كلّ وحدة — فالسحبُ موقوفٌ هنا والعرضُ مفتوح.
        </Notice>
      )}

      {data && roots.length === 0 && (
        <EmptyState
          title="لا هيكل بعد"
          text="ابدأ بإدارةٍ واحدةٍ في الجذر، ثمّ ضع تحتها أقسامَها بالسحب. والمسمّياتُ الوظيفيّةُ تُكتب في «الأوصاف الوظيفية» ثمّ تُسحب من الجانب إلى موضعها."
          action={
            canEdit ? (
              <button
                className="btn btn-primary"
                onClick={() => setEditing({ unit: null, parent: null })}
              >
                أضف أوّل وحدة
              </button>
            ) : undefined
          }
        />
      )}

      {data && roots.length > 0 && (
        <div
          className="row"
          style={{ gap: 'var(--sp-5)', alignItems: 'flex-start', flexWrap: 'wrap' }}
        >
          <div style={{ flex: '1 1 30rem', minWidth: '20rem' }}>
            {/*
              منطقةُ الجذر تُعرض دائمًا لا عند السحب فقط: من يريد إخراجَ
              وحدةٍ من تحت أبيها يحتاج أن يعرف أين يفلتها قبل أن يبدأ السحب.
            */}
            {canEdit && (
              <div
                className="card"
                style={dropStyle(over === 'root')}
                {...zoneProps('root', dropOnRoot)}
              >
                <div className="card-body">
                  <span className="tiny mute">
                    <Icon name="arrowLeft" size={13} /> أفلِت هنا لإخراج الوحدة إلى
                    الجذر — أو لإخراج المسمّى من وحدته.
                  </span>
                </div>
              </div>
            )}

            {roots.map((node) => (
              <UnitNode
                key={node.id}
                node={node}
                depth={0}
                canEdit={canEdit}
                collapsed={collapsed}
                onToggle={toggle}
                over={over}
                drag={drag}
                setDrag={setDrag}
                zoneProps={zoneProps}
                onDropUnit={dropOnUnit}
                onAddChild={(parent) => setEditing({ unit: null, parent })}
                onEdit={(unit) => setEditing({ unit, parent: null })}
                onDetachPosition={(id) => placePosition.mutate({ id, orgUnitId: null })}
              />
            ))}
          </div>

          <div style={{ flex: '0 1 22rem', minWidth: '18rem' }}>
            <LoosePositions
              positions={loose}
              canEdit={canEdit}
              onDragStart={(chip) =>
                setDrag({ kind: 'position', id: chip.id, title: chip.title })
              }
              onDragEnd={() => {
                setDrag(null);
                setOver(null);
              }}
            />
          </div>
        </div>
      )}

      {/*
        الحدُّ يُكتب لا يُكتشف: الوحدةُ في الهيكل شيءٌ وملفُّ الموظّف شيءٌ
        آخر، ورسمُ الهيكل لا ينقل أحدًا إلى وحدةٍ من تلقاء نفسه.
      */}
      {data && (
        <Notice title="رسمُ الهيكل لا ينقل الموظّفين" icon="info">
          وضعُ مسمًّى في وحدةٍ يقول «مكانُ هذا المنصب هنا»، ولا يغيّر وحدةَ
          أحدٍ في ملفّه. ومن يُنقل فعلًا يُنقل من ملفّه في «الموظّفون» —
          وعددُ «خارج الهيكل» أعلاه هو من لم يُنقل بعد.
        </Notice>
      )}

      {editing && (
        <UnitDialog
          unit={editing.unit}
          parent={editing.parent}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   عقدة الوحدة
   ========================================================================== */

function UnitNode({
  node,
  depth,
  canEdit,
  collapsed,
  onToggle,
  over,
  drag,
  setDrag,
  zoneProps,
  onDropUnit,
  onAddChild,
  onEdit,
  onDetachPosition,
}: {
  node: Node;
  depth: number;
  canEdit: boolean;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  over: string | null;
  drag: Drag | null;
  setDrag: (d: Drag | null) => void;
  zoneProps: (key: string, onDrop: () => void) => Record<string, unknown>;
  onDropUnit: (target: Node) => void;
  onAddChild: (parent: UnitDraft) => void;
  onEdit: (unit: UnitDraft) => void;
  onDetachPosition: (id: string) => void;
}) {
  const shut = collapsed.has(node.id);
  const kindDef = ORG_UNIT_KIND_MAP[node.kind as OrgUnitKind];

  /* الوحدةُ لا تُفلَت على نفسها — تُطفأ المنطقةُ بدل أن يُردّ النداء. */
  const self = drag?.kind === 'unit' && drag.id === node.id;
  const lit = over === node.id && !self;

  return (
    <div>
      <div
        className="card"
        style={dropStyle(lit)}
        draggable={canEdit}
        onDragStart={(e) => {
          if (!canEdit) return;
          e.stopPropagation();
          /* بياناتٌ في الحدث ليقبل المتصفّحُ السحبَ أصلًا — والحالةُ هي المصدر. */
          e.dataTransfer.setData('text/plain', node.name);
          e.dataTransfer.effectAllowed = 'move';
          setDrag({ kind: 'unit', id: node.id, name: node.name });
        }}
        onDragEnd={() => setDrag(null)}
        {...(canEdit ? zoneProps(node.id, () => onDropUnit(node)) : {})}
      >
        <div className="card-head">
          {node.children.length > 0 && (
            <button
              className="btn btn-sm"
              onClick={() => onToggle(node.id)}
              aria-expanded={!shut}
              title={shut ? 'افتح' : 'أغلق'}
            >
              <Icon name={shut ? 'chevronLeft' : 'chevronDown'} size={14} />
            </button>
          )}
          <h2 className="card-title">{node.name}</h2>
          <Badge kind={node.kindTone as 'brand'}>{kindDef.label}</Badge>
          {node.department && <Badge kind="info">{node.department}</Badge>}
          {!node.department && (
            <Badge kind="warning">خارج الأقسام</Badge>
          )}
          <span style={{ marginInlineStart: 'auto' }} className="row" dir="rtl">
            {canEdit && (
              <>
                <button className="btn btn-sm" onClick={() => onAddChild(node)}>
                  <Icon name="plus" size={13} />
                  تحتها
                </button>
                <button className="btn btn-sm" onClick={() => onEdit(node)}>
                  <Icon name="edit" size={13} />
                  تعديل
                </button>
              </>
            )}
          </span>
        </div>

        <div className="card-body">
          <div className="row tiny mute" style={{ gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
            <span>
              <Icon name="user" size={12} /> {node.manager ?? 'بلا مدير'}
            </span>
            <span>
              <Icon name="users" size={12} /> <span className="num">{num(node.totalCount)}</span>{' '}
              في الفرع
              {node.directCount !== node.totalCount && (
                <>
                  {' '}
                  (<span className="num">{num(node.directCount)}</span> مباشرةً)
                </>
              )}
            </span>
            {node.costCenter && (
              <span>
                <Icon name="bank" size={12} /> {node.costCenter.name}
              </span>
            )}
            {node.approved > 0 && (
              <span>
                <Icon name="briefcase" size={12} />{' '}
                <span className="num">{num(node.approved)}</span> مقعدًا معتمدًا
              </span>
            )}
          </div>

          {node.positions.length > 0 ? (
            <div
              className="row mt-2"
              style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}
            >
              {node.positions.map((x) => (
                <PositionTag
                  key={x.id}
                  chip={x}
                  canEdit={canEdit}
                  onDragStart={() =>
                    setDrag({ kind: 'position', id: x.id, title: x.title })
                  }
                  onDragEnd={() => setDrag(null)}
                  onDetach={() => onDetachPosition(x.id)}
                />
              ))}
            </div>
          ) : (
            <p className="tiny mute mt-2">
              لا مسمّياتٍ هنا — اسحب واحدًا من الجانب.
            </p>
          )}
        </div>
      </div>

      {!shut && node.children.length > 0 && (
        <div style={branchRail}>
          {node.children.map((child) => (
            <UnitNode
              key={child.id}
              node={child}
              depth={depth + 1}
              canEdit={canEdit}
              collapsed={collapsed}
              onToggle={onToggle}
              over={over}
              drag={drag}
              setDrag={setDrag}
              zoneProps={zoneProps}
              onDropUnit={onDropUnit}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDetachPosition={onDetachPosition}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * قضيبُ الفرع — الخطُّ الذي يقول «ما تحته يتبع ما فوقه».
 *
 * إزاحةٌ وحدَها لا تُقرأ شجرةً: عشرون بكسلًا بين بطاقتين تبدو فرقًا في
 * العرض لا تبعيّةً، وقد بدت كذلك فعلًا في أوّل رسم. والخطُّ الرأسيُّ يقولها
 * بلا كلمة.
 */
const branchRail: React.CSSProperties = {
  marginInlineStart: 'var(--sp-3)',
  paddingInlineStart: 'var(--sp-4)',
  borderInlineStart: '2px solid var(--line)',
};

/* ==========================================================================
   المسمّى الوظيفي
   ========================================================================== */

/**
 * المسمّى بطاقةٌ صغيرةٌ تُسحب.
 *
 * ويُكتب عليها المشغولُ من المعتمَد — «٢ من ٣» — لأنّ من يرسم الهيكلَ
 * يسأل عن الشاغر وهو ينظر إليه، لا بعد أن يفتح شاشةً أخرى.
 */
function PositionTag({
  chip,
  canEdit,
  onDragStart,
  onDragEnd,
  onDetach,
}: {
  chip: PositionChip;
  canEdit: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDetach?: () => void;
}) {
  const short = chip.headcount - chip.filled;

  return (
    <span
      className="row tiny"
      draggable={canEdit}
      onDragStart={(e) => {
        if (!canEdit) return;
        e.stopPropagation();
        e.dataTransfer.setData('text/plain', chip.title);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      style={{
        gap: 6,
        padding: '3px 8px',
        borderRadius: 'var(--r-sm)',
        border: '1px solid var(--line)',
        background: 'var(--bg-2)',
        cursor: canEdit ? 'grab' : 'default',
      }}
      title={chip.reportsTo ? `تتبع ${chip.reportsTo}` : 'بلا رئيسٍ في وصفها'}
    >
      <Icon name="briefcase" size={12} />
      {chip.title}
      <span className="num mute">
        {num(chip.filled)}/{num(chip.headcount)}
      </span>
      {short > 0 && <Badge kind="warning">{num(short)} شاغر</Badge>}
      {short < 0 && <Badge kind="danger">فوق المعتمَد</Badge>}
      {canEdit && onDetach && (
        <button
          className="btn btn-sm"
          onClick={onDetach}
          title="أخرجه من هذه الوحدة"
          style={{ padding: '0 4px' }}
        >
          <Icon name="x" size={12} />
        </button>
      )}
    </span>
  );
}

/** ما لم يوضع بعد — يُعرض على حدةٍ ليُعرف الناقصُ من الموضوع. */
function LoosePositions({
  positions,
  canEdit,
  onDragStart,
  onDragEnd,
}: {
  positions: PositionChip[];
  canEdit: boolean;
  onDragStart: (chip: PositionChip) => void;
  onDragEnd: () => void;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">مسمّيات بلا موضع</h2>
        <span className="tiny mute">{plural(positions.length, WORDS.position)}</span>
      </div>
      <div className="card-body">
        {positions.length === 0 ? (
          <p className="tiny mute">
            كلُّ المسمّيات الفعّالة لها موضعٌ في الهيكل. وما يُكتب جديدًا في
            «الأوصاف الوظيفية» يظهر هنا حتى يُسحب إلى وحدته.
          </p>
        ) : (
          <>
            <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              {positions.map((x) => (
                <PositionTag
                  key={x.id}
                  chip={x}
                  canEdit={canEdit}
                  onDragStart={() => onDragStart(x)}
                  onDragEnd={onDragEnd}
                />
              ))}
            </div>
            <p className="tiny mute mt-2">
              اسحب المسمّى إلى الوحدة التي مكانُه فيها. وما يبقى هنا ليس خطأً
              بالضرورة: منصبٌ لم يُقرَّر قسمُه بعدُ له موضعٌ في القائمة ولا موضعَ
              له في الرسم.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   أدوات
   ========================================================================== */

/**
 * المنطقةُ المضيئةُ تحت المؤشّر.
 *
 * تُرسم بإطارٍ وخلفيّةٍ من رموز الألوان نفسِها — وصنفٌ جديدٌ في ملفّ
 * الأنماط لأجل حالةٍ لا تظهر إلّا أثناء السحب كان سيضيف اسمًا يقرؤه كلُّ
 * الشاشات ولا يستعمله غيرُ هذه.
 */
function dropStyle(lit: boolean): React.CSSProperties {
  return lit
    ? {
        outline: '2px dashed var(--brand-500)',
        outlineOffset: 2,
        background: 'var(--brand-50)',
      }
    : {};
}

function countUnits(nodes: Node[]): number {
  return nodes.reduce((n, x) => n + 1 + countUnits(x.children), 0);
}

function countPositions(nodes: Node[]): number {
  return nodes.reduce(
    (n, x) => n + x.positions.length + countPositions(x.children),
    0,
  );
}

function sumApproved(nodes: Node[]): number {
  return nodes.reduce((n, x) => n + x.approved + sumApproved(x.children), 0);
}
